import { i18n } from '@i18n/translations';
import { t } from 'i18next';
import { toast } from 'sonner';

/**
 * Short window during which the interceptor already toasted a
 * global lifecycle/quota/paywall error (e.g. SUBSCRIPTION_SUSPENDED,
 * SUBSCRIPTION_PAST_DUE). Per-hook `onError` handlers that call the
 * generic `showToast.error.{created,updated,deleted,generic}`
 * helpers skip during this window so the operator sees ONLY the
 * translated lifecycle message — not a redundant "Error al crear
 * Clientes" underneath it.
 *
 * 1500 ms covers React Query's rejection → onError propagation
 * without accidentally swallowing an unrelated toast that fires
 * later in the same session.
 */
let suppressGenericUntilMs = 0;
export const suppressGenericErrorToasts = (ms = 1500): void => {
  suppressGenericUntilMs = Date.now() + ms;
};
export const isGenericErrorToastSuppressed = (): boolean =>
  Date.now() < suppressGenericUntilMs;
const isSuppressed = isGenericErrorToastSuppressed;

// Generic toast messages for CRUD operations
export const showToast = {
  // Success messages
  success: {
    created: (entityKey: string) => {
      const entityName = getEntityName(entityKey);
      return toast.success(t(i18n.COMMON.NOTIFICATION), {
        description: `${entityName} ${t(i18n.COMMON.CREATED_SUCCESSFULLY)}`,
      });
    },

    updated: (entityKey: string) => {
      const entityName = getEntityName(entityKey);
      return toast.success(t(i18n.COMMON.NOTIFICATION), {
        description: `${entityName} ${t(i18n.COMMON.UPDATED_SUCCESSFULLY)}`,
      });
    },

    deleted: (entityKey: string) => {
      const entityName = getEntityName(entityKey);
      return toast.success(t(i18n.COMMON.NOTIFICATION), {
        description: `${entityName} ${t(i18n.COMMON.DELETED_SUCCESSFULLY)}`,
      });
    },

    // Custom success message
    custom: (title: string, description?: string) =>
      toast.success(title, { description }),
  },

  // Error messages
  error: {
    created: (entityKey: string) => {
      if (isSuppressed()) return;
      const entityName = getEntityName(entityKey);
      return toast.error(t(i18n.COMMON.NOTIFICATION), {
        description: `${t(i18n.COMMON.ERROR_CREATING)} ${entityName}`,
      });
    },

    updated: (entityKey: string) => {
      if (isSuppressed()) return;
      const entityName = getEntityName(entityKey);
      return toast.error(t(i18n.COMMON.NOTIFICATION), {
        description: `${t(i18n.COMMON.ERROR_UPDATING)} ${entityName}`,
      });
    },

    deleted: (entityKey: string) => {
      if (isSuppressed()) return;
      const entityName = getEntityName(entityKey);
      return toast.error(t(i18n.COMMON.NOTIFICATION), {
        description: `${t(i18n.COMMON.ERROR_DELETING)} ${entityName}`,
      });
    },

    // Generic error
    generic: () => {
      if (isSuppressed()) return;
      return toast.error(t(i18n.COMMON.NOTIFICATION), {
        description: t(i18n.COMMON.GENERIC_ERROR),
      });
    },

    // Custom error message — also suppressed inside the lifecycle
    // window so pages that catch a mutation failure and fall back
    // to a generic `.custom(t(i18n.COMMON.ERROR))` don't stack on
    // top of the interceptor's translated lifecycle toast. Outside
    // the window (non-lifecycle failures) it fires normally.
    custom: (title: string, description?: string) => {
      if (isSuppressed()) return;
      return toast.error(title, { description });
    },
  },

  // Info messages
  info: (title: string, description?: string) =>
    toast.info(title, { description }),

  // Warning messages
  warning: (title: string, description?: string) =>
    toast.warning(title, { description }),

  // Loading messages (returns toast id for dismissal)
  loading: (message: string) => toast.loading(message),

  // Dismiss a specific toast
  dismiss: (toastId: string | number) => toast.dismiss(toastId),
};

// Entity name getter function - dynamically gets names from translations
export const getEntityName = (
  entityKey: keyof typeof i18n.MENU | string
): string => {
  // If it's a valid MENU key, get the translation
  if (entityKey in i18n.MENU) {
    return t(i18n.MENU[entityKey as keyof typeof i18n.MENU]);
  }

  // Fallback for entities not in MENU (return as-is or provide fallbacks)
  const fallbacks: Record<string, string> = {
    BRANCH_OFFICE: 'Sucursal',
    ROLE: 'Rol',
    NCF: 'NCF',
    RESTAURANT_ENVIRONMENT: 'Ambiente',
    RESTAURANT_TABLE: 'Mesa',
    RESTAURANT_DECORATION: 'Decoración',
  };

  return fallbacks[entityKey] || entityKey;
};

// Predefined entity keys for common entities
export const ENTITY_KEYS = {
  CATEGORY: 'CATEGORIES',
  PRODUCT: 'PRODUCTS',
  USER: 'USERS',
  MODIFIER: 'MODIFIERS',
  WAREHOUSE: 'WAREHOUSES',
  BRANCH_OFFICE: 'BRANCH_OFFICE',
  ROLE: 'ROLE',
  SHIFT: 'SHIFTS',
  IMAGE: 'IMAGE',
  CUSTOMER: 'CUSTOMERS',
  PROVIDER: 'PROVIDERS',
  BUSINESS: 'BUSINESS',
  PROVIDERS: 'PROVIDERS',
  PURCHASE_ORDER: 'PURCHASE_ORDERS',
  SETTINGS: 'SETTINGS',
  NCF: 'NCF',
  ROLE_PERMISSIONS: 'ROLES_PERMISSIONS',
  RESTAURANT_ENVIRONMENT: 'RESTAURANT_ENVIRONMENT',
  RESTAURANT_TABLE: 'RESTAURANT_TABLE',
  RESTAURANT_DECORATION: 'RESTAURANT_DECORATION',
} as const;
